/**
 * InputSelect 组件 - Location/Hashtag 输入选择器
 *
 * 功能概述：
 * - 支持两种搜索类型：Location（地点）和 Hashtag（话题标签）
 * - PC端使用 Popover 展示搜索结果
 * - 移动端使用底部抽屉（Drawer）展示搜索结果
 * - 登录状态控制：未登录时点击输入框或按钮会触发登录弹窗
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Input, Popover } from 'antd';
import { CloseCircleFilled, InstagramOutlined } from '@ant-design/icons';
import { useTranslation } from 'next-i18next';
import { cn } from '@/lib/utils';
import useCheckIsMobile from '@/lib/hooks/useCheckIsMobile';
import { useDebounce } from '@/lib/hooks/utils/useDebounce';
import { InputSelectProps, SearchResultItem } from './types';
import { searchHashtag, searchLocation } from './api';
import { ResultList } from './ResultList';

const InputSelect: React.FC<InputSelectProps> = ({
  type = 'location',
  placeholder,
  onSearch,
  isLogin = false,
  onTriggerLogin,
}) => {
  const { t } = useTranslation('common');
  const { isMobile } = useCheckIsMobile(1024);
  const inputRef = useRef<any>(null);
  const drawerInputRef = useRef<any>(null);
  const shouldSearchAfterLogin = useRef(false); // 标记登录后是否需要搜索

  // 状态管理
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isComposing, setIsComposing] = useState(false); // 合成事件标识

  // 防抖搜索值
  const debouncedSearchValue = useDebounce(inputValue, 500);

  // 执行搜索
  const performSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      try {
        const results = type === 'location' ? await searchLocation(query) : await searchHashtag(query);
        setSearchResults(results);
        setHighlightedIndex(-1);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [type]
  );

  // 监听防抖后的搜索值变化
  useEffect(() => {
    if (isLogin && (popoverVisible || drawerVisible)) {
      void performSearch(debouncedSearchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, isLogin, popoverVisible, drawerVisible]);

  // 移动端 Drawer 打开时自动聚焦（仅在首次打开时）
  useEffect(() => {
    if (isMobile && drawerVisible) {
      // 延迟聚焦，等待 Drawer 动画完成
      const timer = setTimeout(() => {
        drawerInputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isMobile, drawerVisible]);

  // 监听登录状态变化，登录成功后触发搜索
  useEffect(() => {
    if (isLogin && shouldSearchAfterLogin.current && inputValue.trim()) {
      shouldSearchAfterLogin.current = false;

      // 登录成功后打开 Popover/Drawer 并触发搜索
      if (isMobile) {
        setDrawerVisible(true);
      } else {
        setPopoverVisible(true);
      }

      void performSearch(inputValue);
    }
  }, [isLogin, inputValue, isMobile, performSearch]);

  // 输入框点击事件
  const handleInputClick = () => {
    // 移动端：未登录时弹出登录框，已登录时打开抽屉
    if (isMobile) {
      if (!isLogin) {
        onTriggerLogin?.();
        return;
      }
      setDrawerVisible(true);
      if (inputValue.trim()) {
        void performSearch(inputValue);
      }
      return;
    }

    // PC端：未登录时允许正常输入
    if (!isLogin) {
      return;
    }

    setPopoverVisible(true);
    // 如果已有输入值，立即触发搜索
    if (inputValue.trim()) {
      void performSearch(inputValue);
    }
  };

  // 输入框变化事件
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // 如果输入为空，清空结果
    if (!value.trim()) {
      setSearchResults([]);
      setHighlightedIndex(-1);
    }
  };

  // 合成事件开始（输入法开始输入）
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  // 合成事件结束（输入法完成输入）
  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposing(false);
    // 合成结束后，手动触发一次输入处理
    const value = (e.target as HTMLInputElement).value;
    setInputValue(value);
  };

  // 清空按钮点击
  const handleClear = () => {
    setInputValue('');
    setSearchResults([]);
    setHighlightedIndex(-1);
    // 移动端清空时不关闭抽屉，聚焦抽屉内输入框
    if (isMobile) {
      drawerInputRef.current?.focus();
    } else {
      setPopoverVisible(false);
      inputRef.current?.focus();
    }
  };

  // 列表项点击
  const handleItemClick = (item: SearchResultItem) => {
    onSearch?.(item);
    setPopoverVisible(false);
    setDrawerVisible(false);
    setInputValue('');
    setSearchResults([]);
    setHighlightedIndex(-1);
  };

  // 列表项 hover
  const handleItemHover = (index: number) => {
    setHighlightedIndex(index);
  };

  // 键盘事件处理
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 如果正在输入法输入中，除了ESC其他键盘事件都忽略
    if (isComposing && e.key !== 'Escape') {
      return;
    }

    // 未登录时，回车触发登录
    if (!isLogin) {
      if (e.key === 'Enter') {
        e.preventDefault();
        // 标记需要在登录后搜索
        shouldSearchAfterLogin.current = true;
        onTriggerLogin?.();
      }
      return;
    }

    // 已登录但无结果时，禁止回车
    if (searchResults.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
          handleItemClick(searchResults[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setPopoverVisible(false);
        setDrawerVisible(false);
        break;

      default:
        break;
    }
  };

  // "Find Email" 按钮点击
  const handleBtnClick = () => {
    if (!isLogin) {
      // 标记需要在登录后搜索
      shouldSearchAfterLogin.current = true;
      onTriggerLogin?.();
      return;
    }

    // 移动端点击按钮打开抽屉
    if (isMobile) {
      setDrawerVisible(true);
      if (inputValue.trim()) {
        void performSearch(inputValue);
      }
    }
  };

  // Popover 内容
  const popoverContent = (
    <div className="max-h-[342px] overflow-y-auto">
      {isSearching ? (
        <div className="py-6 text-center text-gray-400">
          <p>{t('Searching...')}</p>
        </div>
      ) : (
        <ResultList
          results={searchResults}
          highlightedIndex={highlightedIndex}
          onItemClick={handleItemClick}
          onItemHover={handleItemHover}
          isMobile={false}
        />
      )}
    </div>
  );

  // 关闭 Drawer 的处理函数
  const handleCloseDrawer = useCallback(() => {
    setDrawerVisible(false);
    // 关闭 Drawer 时清空输入值和搜索结果
    setInputValue('');
    setSearchResults([]);
    setHighlightedIndex(-1);
    // 移动端关闭抽屉后，确保原输入框不聚焦
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }, []);

  return (
    <>
      <div className="flex items-center max-w-[610px] rounded-md overflow-hidden mx-auto pt-10">
        <Popover
          content={popoverContent}
          trigger="click"
          arrow={false}
          open={!isMobile && popoverVisible}
          onOpenChange={(visible) => {
            if (!visible) {
              setPopoverVisible(false);
            }
          }}
          placement="bottom"
          classNames={{ body: 'w-[1000px] max-w-[610px] !p-0 overflow-hidden' }}
        >
          <Input
            ref={inputRef}
            className="flex-1 h-14"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onKeyDown={handleKeyDown}
            onClick={handleInputClick}
            onFocus={(e) => {
              // 移动端阻止聚焦，仅保持点击事件
              if (isMobile) {
                e.target.blur();
              }
            }}
            readOnly={isMobile}
            prefix={<InstagramOutlined className="text-[22px] mr-1" />}
            suffix={
              <>
                {/* 只有登录后且有输入内容时才显示清空按钮 */}
                {isLogin && inputValue && (
                  <CloseCircleFilled
                    className="text-gray-400 cursor-pointer hover:text-gray-600 mr-2"
                    onClick={handleClear}
                  />
                )}
                {/* PC端：登录后且有输入内容时隐藏按钮 */}
                {!(isLogin && inputValue) && (
                  <Button
                    className={cn('h-[38px] primary-btn px-9 hidden lg:block')}
                    type="primary"
                    onClick={handleBtnClick}
                    loading={isSearching}
                  >
                    {t('Find Email')}
                  </Button>
                )}
              </>
            }
          />
        </Popover>
      </div>

      {/* 移动端按钮 */}
      <Button
        className={cn(
          'h-[38px] primary-btn max-w-[610px] mx-auto px-9 block lg:hidden w-full mt-4 max-w-[610px] mx-auto'
        )}
        type="primary"
        onClick={handleBtnClick}
        loading={isSearching}
      >
        {t('Find Email')}
      </Button>

      {/* 移动端抽屉 */}
      <Drawer
        title={null}
        placement="bottom"
        closable={false}
        onClose={handleCloseDrawer}
        open={isMobile && drawerVisible}
        height="100%"
        styles={{
          body: { padding: 0 },
          header: { display: 'none' },
        }}
      >
        <div className="flex flex-col h-full">
          {/* 搜索框区域 */}
          <div className="border-b border-[#d9d9d9] px-4 py-2.5 flex items-center gap-3">
            {/* 返回按钮 */}
            <button
              onClick={handleCloseDrawer}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-[#0c0c0c] hover:opacity-70 transition-opacity"
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15.5 19L8.5 12L15.5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <Input
              ref={drawerInputRef}
              className="h-11 flex-1"
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onKeyDown={handleKeyDown}
              suffix={
                <CloseCircleFilled
                  className={cn('text-gray-400 cursor-pointer hover:text-gray-600', !inputValue && 'invisible')}
                  onClick={handleClear}
                />
              }
            />
          </div>

          {/* 结果列表区域 */}
          <div className="flex-1 overflow-y-auto">
            {isSearching ? (
              <div className="py-8 text-center text-gray-400">
                <p>{t('Searching...')}</p>
              </div>
            ) : (
              <ResultList
                results={searchResults}
                highlightedIndex={highlightedIndex}
                onItemClick={handleItemClick}
                onItemHover={handleItemHover}
                isMobile={true}
              />
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default InputSelect;
